import { jsx as _jsx } from "react/jsx-runtime";
import { Tab as MuiTab, Tabs as MuiTabs, styled } from "@mui/material";
import { Children, cloneElement, createContext, isValidElement, useContext, useState } from "react";
const TabsContext = /*#__PURE__*/ createContext(undefined);
const useTabsContext = ()=>{
    const context = useContext(TabsContext);
    if (!context) {
        throw new Error('Tabs components must be used within a TabsProvider');
    }
    return context;
};
export const useTabs = ()=>useTabsContext();
export const Tabs = (props)=>{
    const [internalIndex, setInternalIndex] = useState(props.defaultIndex ?? 0);
    /** Controlled when `index` is set; the owner then drives renders via `onIndexChange`. */ const activeIndex = props.index ?? internalIndex;
    const setActiveIndex = (index)=>{
        props.onIndexChange?.(index);
        if (props.index === undefined) setInternalIndex(index);
    };
    return /*#__PURE__*/ _jsx(TabsContext.Provider, {
        value: {
            activeIndex,
            setActiveIndex
        },
        children: props.children
    });
};
Tabs.displayName = 'Tabs';
/** TODO:: audit the use of this in transactions and consider removing the added div layer. */ const StyledTabList = styled('div')(({ theme })=>({
        borderBottom: `${theme.spacing(0.125)} solid`,
        borderColor: theme.palette.divider
    }));
export const TabList = (props)=>{
    const tabs = useTabsContext();
    const onChange = (_, newIndex)=>{
        tabs.setActiveIndex(newIndex);
    };
    return /*#__PURE__*/ _jsx(StyledTabList, {
        children: /*#__PURE__*/ _jsx(MuiTabs, {
            "aria-label": props['aria-label'],
            onChange: onChange,
            scrollButtons: "auto",
            value: tabs.activeIndex,
            variant: "scrollable",
            children: Children.map(props.children, (child, index)=>{
                if (/*#__PURE__*/ isValidElement(child)) {
                    return /*#__PURE__*/ cloneElement(child, {
                        'aria-controls': `tabpanel-${index}`,
                        id: `tab-${index}`,
                        ...child.props
                    });
                }
                return child;
            })
        })
    });
};
TabList.displayName = 'TabList';
export const Tab = ({ title, ...props })=>{
    return /*#__PURE__*/ _jsx(MuiTab, {
        label: title,
        ...props
    });
};
Tab.displayName = 'Tab';
const fillStyles = {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    /**
   * Both axes must opt out of min-*: auto. Without minWidth a wide child (a
   * DataGrid whose columns exceed the viewport) forces the panel past 100%
   * instead of letting the child scroll internally.
   */ minHeight: 0,
    minWidth: 0
};
const StyledTabPanelRoot = styled('div', {
    shouldForwardProp: (prop)=>prop !== 'fullHeight'
})(({ fullHeight })=>({
        // Guard the fill styles behind :not([hidden]) so inactive panels stay hidden.
        ...fullHeight && {
            '&:not([hidden])': fillStyles
        }
    }));
const StyledTabPanel = styled('div', {
    shouldForwardProp: (prop)=>prop !== 'fullHeight' && prop !== 'disablePaddingBottom'
})(({ disablePaddingBottom, fullHeight, theme })=>({
        padding: theme.spacing(2, 0),
        ...disablePaddingBottom && {
            paddingBottom: 0
        },
        ...fullHeight && fillStyles
    }));
export const TabPanel = ({ disablePaddingBottom, fullHeight, ...props })=>{
    const tabs = useTabsContext();
    const isActive = tabs.activeIndex === props.index;
    return /*#__PURE__*/ _jsx(StyledTabPanelRoot, {
        "aria-labelledby": `tab-${props.index}`,
        fullHeight: fullHeight,
        hidden: !isActive,
        id: `tabpanel-${props.index}`,
        role: "tabpanel",
        children: isActive && /*#__PURE__*/ _jsx(StyledTabPanel, {
            disablePaddingBottom: disablePaddingBottom,
            fullHeight: fullHeight,
            children: props.children
        })
    });
};
TabPanel.displayName = 'TabPanel';

//# sourceMappingURL=Tabs.js.map